import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  inject,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  input,
  viewChild,
  output,
  InputSignal,
  OutputEmitterRef
} from '@angular/core';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ListComponent } from '../../../../../components/list/list.component';
import { ListItemComponent } from '../../../../../components/list/list-item.component';
import { ListItemValueComponent } from '../../../../../components/list/list-item-value.component';
import { ListItemLabelComponent } from '../../../../../components/list/list-item-label.component';
import { HoldingsDripReinvestToggleComponent } from '../../../../accounts/dividends/holdings-drip-reinvest-toggle/holdings-drip-reinvest-toggle.component';
import { DividendsRelatedAccountsComponent } from '../../../../accounts/dividends/related-accounts/dividends-related-accounts.component';

import { DatePipe } from '../../../../../pipes/date.pipe';
import { NumberPipe } from '../../../../../pipes/number.pipe';
import { CurrencyPipe } from '../../../../../pipes/currency.pipe';
import { PercentPipe } from '../../../../../pipes/percent.pipe';
import { PlaceholderPipe } from '../../../../../pipes/placeholder.pipe';

import { DetailedQuote } from '../../../../../domain/quote.domain';
import { Frequency } from '../../domain/frequency.enum';
import { QuoteIssueType } from '../../domain';
import { SecurityService } from '../../../../../services/security.service';
import { BadgeComponent } from '../../../../../components/badge/badge.component';

@Component({
  selector: 'td-wb-detailed-quote-more-details',
  templateUrl: 'detailed-quote-more-details.component.html',
  styleUrls: ['detailed-quote-more-details.component.scss'],
  imports: [
    NgTemplateOutlet,
    TranslateDirective,
    ListComponent,
    ListItemComponent,
    ListItemLabelComponent,
    ListItemValueComponent,
    DatePipe,
    NumberPipe,
    CurrencyPipe,
    PercentPipe,
    PlaceholderPipe,
    BadgeComponent,
    TranslatePipe,
    HoldingsDripReinvestToggleComponent,
    DividendsRelatedAccountsComponent
  ]
})
export class DetailedQuoteMoreDetailsComponent implements OnChanges {

  readonly quote: InputSignal<DetailedQuote | undefined> = input<DetailedQuote | undefined>(undefined);
  readonly showReinvestToggle: InputSignal<boolean> = input<boolean>(false);
  readonly isNarrowView: InputSignal<boolean> = input<boolean>(false);
  readonly reinvestSelection: InputSignal<boolean> = input<boolean>(false);
  readonly reinvestToggleId: InputSignal<string> = input<string>('');
  readonly reinvestChange: OutputEmitterRef<boolean> = output<boolean>();

  readonly templateRefStock = viewChild<TemplateRef<unknown>>('stock');
  readonly templateRefIndex = viewChild<TemplateRef<unknown>>('index');
  readonly templateRefMf = viewChild<TemplateRef<unknown>>('mf');
  readonly templateRefOption = viewChild<TemplateRef<unknown>>('option');
  readonly templateRefEtf = viewChild<TemplateRef<unknown>>('etf');

  currentTemplate: TemplateRef<unknown>;
  readonly placeholder: string = '\u2014';

  private readonly translateService = inject(TranslateService);
  private readonly securityService = inject(SecurityService);

  /**
   * Set template on quote changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    const change = changes['quote'];
    if (change !== undefined) {
      this.currentTemplate = this.getIssueTypeTemplate();
    }
  }

  /**
   * This function returns frequency header text based on dividend frequency
   *
   * @return
   */
  getDividendFrequencyHeader(): string {
    let frequency = '';

    const quote = this.quote();
    switch (quote.dividend.frequency) {
      case Frequency.IRREGULAR:
      case Frequency.MONTHLY:
      case Frequency.QUARTERLY:
      case Frequency.BIANNUAL:
      case Frequency.TRIANNUAL:
      case Frequency.YEARLY:
        frequency = quote.dividend.frequency.toUpperCase() + '_';
        break;
    }

    frequency = this.translateService.instant('tdWb.markets.quote.' + frequency + 'DIVIDEND');
    frequency += '/';
    frequency += this.translateService.instant('tdWb.markets.quote.YIELD');
    return frequency;
  }

  getLoadType(): string {
    const quote = this.quote();
    switch  (quote.loadType) {
      case 'BE':
      case 'DM':
      case 'DO':
      case 'FE':
      case 'IS':
      case 'LL':
      case 'NL':
      case 'SL':
      case 'VS':
        return this.translateService.instant('tdWb.markets.quote.mf.LOAD_TYPE_' + quote.loadType);
    }
    return undefined;
  }

  userHasDripPermission(): boolean {
    return this.securityService.hasPermission('DIVIDEND_REINVESTMENT_PLAN');
  }

  onReinvestChange(value: boolean): void {
    this.reinvestChange.emit(value);
  }

  /**
   * get template based on issue type
   *
   * @returns template to be used in template outlet
   */
  private getIssueTypeTemplate(): TemplateRef<unknown> | undefined {
    switch (this.quote().issueType) {
      case QuoteIssueType.STOCK:
        return this.templateRefStock();
      case QuoteIssueType.ETF:
        return this.templateRefEtf();
      case QuoteIssueType.INDEX:
        return this.templateRefIndex();
      case QuoteIssueType.OPTION:
        return this.templateRefOption();
      case QuoteIssueType.MF:
        return this.templateRefMf();
      default:
        return undefined;
    }
  }
}
